import React, { useState } from 'react';
import Styles from './Step4CustomerVoice.module.scss';
import BSButton from 'Components/Button/BSButton';
import { FiPlus, FiX, FiLink2, FiSearch, FiStar, FiCheck, FiLoader } from 'react-icons/fi';
import { postRequest } from 'helpers/axios';
import { success as successToast, error as errorToast } from 'helpers/toaster';

const SUPPORTED_SOURCES = [
  { name: 'Trustpilot', domain: 'trustpilot.com', icon: '⭐' },
  { name: 'App Store', domain: 'apps.apple.com', icon: '🍎' },
  { name: 'Play Store', domain: 'play.google.com', icon: '▶️' },
  { name: 'GetApp', domain: 'getapp.com', icon: '📱' },
  { name: 'AlternativeTo', domain: 'alternativeto.net', icon: '🔄' },
  { name: 'Clutch', domain: 'clutch.co', icon: '🏆' },
  { name: 'Custom Website', domain: '', icon: '🌐' },
];

const Step4CustomerVoice = ({ data, onUpdate, onNext, onPrevious, onSaveAndExit, onSaveAndContinue, brandId }) => {
  const [customerVoiceLinks, setCustomerVoiceLinks] = useState(
    data.customerVoiceLinks || []
  );
  const [newLink, setNewLink] = useState('');
  const [customerVoiceText, setCustomerVoiceText] = useState(
    data.customerVoiceText || ''
  );
  const [scrapedReviews, setScrapedReviews] = useState(data.customerVoiceAnalysis?.reviews || []);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingUrl, setAnalyzingUrl] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(data.customerVoiceAnalysis || null);
  const [isSaving, setIsSaving] = useState(false);

  const addLink = () => {
    if (newLink.trim()) {
      setCustomerVoiceLinks([...customerVoiceLinks, newLink.trim()]);
      setNewLink('');
    }
  };

  const removeLink = (index) => {
    setCustomerVoiceLinks(customerVoiceLinks.filter((_, i) => i !== index));
  };

  const detectSource = (url) => {
    for (const source of SUPPORTED_SOURCES) {
      if (source.domain && url.includes(source.domain)) {
        return source;
      }
    }
    return SUPPORTED_SOURCES[SUPPORTED_SOURCES.length - 1]; // Custom Website
  };

  const analyzeUrl = async (url) => {
    if (!brandId) {
      errorToast('Please complete earlier steps first to create a brand profile');
      return;
    }

    setIsAnalyzing(true);
    setAnalyzingUrl(url);
    setAnalysisResult(null);

    try {
      const response = await postRequest(
        `brand_profiles/${brandId}/brand_intelligence/scrape_reviews`,
        { url }
      );

      if (response.data.success) {
        const result = response.data.data;
        setAnalysisResult(result);
        setScrapedReviews(result.reviews || []);
        onUpdate({ customerVoiceAnalysis: result });
        successToast(`Found ${result.reviews?.length || 0} reviews from ${result.source_name}`);
      } else {
        errorToast(response.data.error || 'Failed to analyze URL');
      }
    } catch (error) {
      console.error('Analysis error:', error);
      errorToast(error.response?.data?.error || 'Failed to analyze URL');
    } finally {
      setIsAnalyzing(false);
      setAnalyzingUrl(null);
    }
  };

  const saveReviewsAsTraining = async () => {
    if (!brandId || scrapedReviews.length === 0) return;

    setIsSaving(true);
    try {
      const response = await postRequest(
        `brand_profiles/${brandId}/brand_intelligence/save_reviews`,
        {
          reviews: scrapedReviews,
          source_url: analysisResult?.url,
          source_name: analysisResult?.source_name
        }
      );

      if (response.data.success) {
        successToast(`Saved ${response.data.data.saved_count} reviews for training`);
        setScrapedReviews([]);
        setAnalysisResult(null);
      } else {
        errorToast(response.data.error || 'Failed to save reviews');
      }
    } catch (error) {
      console.error('Save error:', error);
      errorToast('Failed to save reviews');
    } finally {
      setIsSaving(false);
    }
  };

  const handleContinue = () => {
    const currentData = {
      customerVoiceLinks,
      customerVoiceText,
      customerVoiceAnalysis: analysisResult,
    };

    onUpdate(currentData);

    // Pass data directly to save (avoids async state update issues)
    if (onSaveAndContinue) {
      onSaveAndContinue(currentData);
    } else {
      onNext();
    }
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FiStar
          key={i}
          className={i <= rating ? Styles.starFilled : Styles.starEmpty}
        />
      );
    }
    return <span className={Styles.stars}>{stars}</span>;
  };

  return (
    <div className={Styles.container}>
      <div className={Styles.header}>
        <h2 className={Styles.title}>Audience</h2>
        <p className={Styles.subtitle}>
          Add customer testimonials, reviews, or feedback to help AI understand
          your target audience's perspective and language
        </p>
      </div>

      <div className={Styles.formContent}>
        {/* Supported Sources Info */}
        <div className={Styles.sourcesInfo}>
          <div className={Styles.sourcesLabel}>Supported Review Sources:</div>
          <div className={Styles.sourcesList}>
            {SUPPORTED_SOURCES.slice(0, -1).map((source) => (
              <span key={source.name} className={Styles.sourceTag}>
                {source.icon} {source.name}
              </span>
            ))}
          </div>
        </div>

        {/* Links to Reviews/Testimonials */}
        <div className={Styles.section}>
          <label className={Styles.label}>
            Review/Testimonial URLs
          </label>
          <div className={Styles.linkInputWrapper}>
            <div className={Styles.iconPrefix}>
              <FiLink2 />
            </div>
            <input
              type="url"
              className={Styles.linkInput}
              placeholder="https://trustpilot.com/review/your-company"
              value={newLink}
              onChange={(e) => setNewLink(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addLink();
                }
              }}
            />
            <button
              type="button"
              className={Styles.addBtn}
              onClick={addLink}
            >
              <FiPlus />
              Add
            </button>
          </div>
          <p className={Styles.helpText}>
            Add links to review pages - we'll extract and analyze customer feedback automatically
          </p>
        </div>

        {/* Added Links List */}
        {customerVoiceLinks.length > 0 && (
          <div className={Styles.linksList}>
            {customerVoiceLinks.map((link, index) => {
              const source = detectSource(link);
              const isCurrentlyAnalyzing = analyzingUrl === link;
              return (
                <div key={index} className={Styles.linkItem}>
                  <span className={Styles.sourceIcon}>{source.icon}</span>
                  <span className={Styles.linkText}>{link}</span>
                  <div className={Styles.linkActions}>
                    <button
                      type="button"
                      className={Styles.analyzeBtn}
                      onClick={() => analyzeUrl(link)}
                      disabled={isAnalyzing}
                    >
                      {isCurrentlyAnalyzing ? (
                        <>
                          <FiLoader className={Styles.spinner} />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <FiSearch />
                          Analyze
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      className={Styles.removeLinkBtn}
                      onClick={() => removeLink(index)}
                    >
                      <FiX />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Analysis Results */}
        {analysisResult && (
          <div className={Styles.analysisResults}>
            <div className={Styles.analysisHeader}>
              <div>
                <h4>Analysis Results - {analysisResult.source_name}</h4>
                <p className={Styles.analysisSummary}>{analysisResult.summary}</p>
                {analysisResult.sentiment && (
                  <span className={`${Styles.sentimentBadge} ${Styles[analysisResult.sentiment]}`}>
                    {analysisResult.sentiment}
                  </span>
                )}
              </div>
              {scrapedReviews.length > 0 && (
                <BSButton
                  classList={Styles.saveBtn}
                  onClick={saveReviewsAsTraining}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : `Save ${scrapedReviews.length} Reviews for Training`}
                </BSButton>
              )}
            </div>

            {/* Common Themes */}
            {analysisResult.common_themes?.length > 0 && (
              <div className={Styles.themes}>
                <span className={Styles.themesLabel}>Common themes:</span>
                {analysisResult.common_themes.map((theme, i) => (
                  <span key={i} className={Styles.themeTag}>{theme}</span>
                ))}
              </div>
            )}

            {/* Reviews List */}
            {scrapedReviews.length > 0 && (
              <div className={Styles.reviewsList}>
                {scrapedReviews.slice(0, 10).map((review, index) => (
                  <div key={index} className={Styles.reviewCard}>
                    <div className={Styles.reviewHeader}>
                      {review.author && (
                        <span className={Styles.reviewAuthor}>{review.author}</span>
                      )}
                      {renderStars(review.rating)}
                      {review.date && (
                        <span className={Styles.reviewDate}>{review.date}</span>
                      )}
                    </div>
                    <p className={Styles.reviewText}>{review.text}</p>
                  </div>
                ))}
                {scrapedReviews.length > 10 && (
                  <p className={Styles.moreReviews}>
                    +{scrapedReviews.length - 10} more reviews
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Customer Voice Text */}
        <div className={Styles.inputGroup}>
          <label htmlFor="customerVoiceText" className={Styles.label}>
            Paste Customer Feedback (Optional)
          </label>
          <textarea
            id="customerVoiceText"
            className={Styles.textarea}
            placeholder="Paste testimonials, reviews, or customer feedback here..."
            rows={6}
            value={customerVoiceText}
            onChange={(e) => setCustomerVoiceText(e.target.value)}
          />
          <p className={Styles.helpText}>
            Manually paste reviews if you can't provide a URL
          </p>
        </div>

        {/* Info Box */}
        <div className={Styles.infoBox}>
          <div className={Styles.infoTitle}>Why is this important?</div>
          <div className={Styles.infoText}>
            Customer voice helps AI understand how your audience talks about
            their problems, what language they use, and what outcomes they value
            most. This makes generated content more relatable and effective.
          </div>
        </div>
      </div>

      <div className={Styles.footer}>
        <button
          type="button"
          className={Styles.previousBtn}
          onClick={onPrevious}
        >
          Previous
        </button>
        <button type="button" className={Styles.saveExitBtn} onClick={onSaveAndExit}>
          Save & Exit
        </button>
        <div className={Styles.stepIndicator}>Step 6 of 7</div>
        <BSButton classList={Styles.continueBtn} onClick={handleContinue}>
          Save & Continue
        </BSButton>
      </div>
    </div>
  );
};

export default Step4CustomerVoice;
